import { Link } from "@remix-run/react";
import { isCourseFree } from "@repo/shared";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import administrativaPoster from "~/assets/illustrations/administrativa.png";
import contadorPoster from "~/assets/illustrations/contador.png";
import pymePoster from "~/assets/illustrations/pyme.png";
import repartidorPoster from "~/assets/illustrations/repartidor.png";
import { cn } from "~/lib/utils";
import { resolveDirectEnrollHref } from "~/utils/courseLinks";

import { useDrawOnView } from "../CuadernoVivo/useDrawOnView";
import { WashiTape } from "../CuadernoVivo/WashiTape";

import { HeroKineticField } from "./HeroKineticField";

import type { SupportedLanguages } from "@repo/shared";

// Clips animados por IA (8s, cuadrados 1:1), servidos desde Bunny CDN. Son las 4
// personas del carousel original. Si más adelante hay clips para docente/almacenero/
// logística/recepcionista, se agregan acá y rotan igual.
const HERO_MEDIA = [
  {
    video: "https://academia-web-assets.b-cdn.net/hero-slider/pyme-anim-square.mp4",
    poster: pymePoster,
  },
  {
    video: "https://academia-web-assets.b-cdn.net/hero-slider/administrativa-anim-square.mp4",
    poster: administrativaPoster,
  },
  {
    video: "https://academia-web-assets.b-cdn.net/hero-slider/contador-anim-square.mp4",
    poster: contadorPoster,
  },
  {
    video: "https://academia-web-assets.b-cdn.net/hero-slider/repartidor-anim-square.mp4",
    poster: repartidorPoster,
  },
];

/**
 * Ilustración del hero: rota por los clips animados con un cross-dissolve DIFUMINADO
 * (opacidad + blur + micro-zoom), en orden aleatorio sin repetir dejando pasar ≥2
 * distintos antes de poder repetir. Doble buffer (2 <video>): el oculto precarga el
 * siguiente. No es carousel (sin flechas ni puntos). Respeta reduced-motion.
 */
function HeroIllustration({ note }: { note: string }) {
  const vidA = useRef<HTMLVideoElement>(null);
  const vidB = useRef<HTMLVideoElement>(null);
  const [mediaA, setMediaA] = useState(HERO_MEDIA[0]);
  const [mediaB, setMediaB] = useState(HERO_MEDIA[1]);
  const [activeA, setActiveA] = useState(true);
  const [reduced, setReduced] = useState(false);
  const activeRef = useRef(true);
  activeRef.current = activeA;
  const historyRef = useRef<number[]>([0, 1]);

  const pickNext = () => {
    const recent = historyRef.current.slice(-2);
    const choices = HERO_MEDIA.map((_, i) => i).filter((i) => !recent.includes(i));
    const idx = choices[Math.floor(Math.random() * choices.length)];
    historyRef.current.push(idx);
    if (historyRef.current.length > 4) historyRef.current.shift();
    return idx;
  };

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setReduced(true);
      vidA.current?.pause();
      return;
    }
    vidA.current?.play().catch(() => {});
    const timer = setInterval(() => {
      const showingA = activeRef.current;
      const incoming = showingA ? vidB.current : vidA.current;
      const outgoing = showingA ? vidA.current : vidB.current;
      if (!incoming) return;
      // El elemento oculto ya tiene el próximo clip precargado → play + cross-dissolve.
      incoming.currentTime = 0;
      incoming.play().catch(() => {});
      setActiveA(!showingA);
      window.setTimeout(() => outgoing?.pause(), 1200);
      // Precargar el clip que sigue en el que quedó oculto, para el próximo ciclo.
      const nextMedia = HERO_MEDIA[pickNext()];
      window.setTimeout(() => (showingA ? setMediaA(nextMedia) : setMediaB(nextMedia)), 1250);
    }, 7400);
    return () => clearInterval(timer);
  }, []);

  const layer = (active: boolean) =>
    cn(
      "absolute inset-0 h-full w-full object-cover transition-[opacity,filter,transform] duration-[1500ms] ease-in-out",
      active ? "z-10 scale-100 opacity-100 blur-0" : "z-0 scale-[1.04] opacity-0 blur-[8px]",
    );

  return (
    // `data-fab-ignore`: acá adentro los clips rotan solos cada pocos segundos. Las
    // acciones flotantes vigilan el DOM para detectar barras fijas, y sin esta marca
    // cada cuadro de esta animación les disparaba una medición. Ver FloatingActions.
    <div
      data-fab-ignore
      className="relative order-first mx-auto w-full max-w-[260px] sm:max-w-[360px] md:order-none md:max-w-none"
    >
      {/* La cinta siempre al frente (z-30), pisando el borde superior del video */}
      <WashiTape variant={0} className="!z-30" />
      {/* Clips cuadrados 1:1 → calzan justo en el contenedor cuadrado, sin barras */}
      <div className="landing-media-paper relative aspect-square w-full overflow-hidden rounded-[12px] shadow-landing-md">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- decorativo, sin audio */}
        <video
          ref={vidA}
          src={mediaA.video}
          poster={mediaA.poster}
          className={layer(activeA)}
          muted
          loop
          playsInline
          autoPlay={!reduced}
          preload="auto"
        />
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- decorativo, sin audio */}
        <video
          ref={vidB}
          src={mediaB.video}
          poster={mediaB.poster}
          className={layer(!activeA)}
          muted
          loop
          playsInline
          preload="auto"
        />
      </div>
      {/* Nota manuscrita: DEBAJO del video (sobre la crema, legible), no encima.
          `data-hero-note` es el TECHO del solapamiento de la card de transición: esa
          card se sube hasta asomar media altura sobre el fold, pero nunca por encima
          del borde inferior de esta nota. Ver EscalonCero.tsx. */}
      <span
        data-hero-note
        className="landing-hand absolute -bottom-7 right-1 z-30 hidden rotate-[-3deg] whitespace-nowrap text-[17px] font-semibold text-landing-teal md:block"
      >
        ← {note}
      </span>
    </div>
  );
}

/**
 * Launch hero — full-viewport en desktop, mensaje único (gancho universal). La frase
 * clave lleva un círculo a lápiz rojo que se dibuja solo; partículas de tinta detrás.
 * La ilustración se anima y rota sola (ver HeroIllustration).
 */
export function LaunchHero() {
  const { t, i18n } = useTranslation();
  const circleRef = useDrawOnView<HTMLSpanElement>(0.1);
  // Este renglón habla de la plataforma, no de un curso: por eso dice "credencial
  // verificable" y no "certificado". Certificado entregan los cursos pagos; los gratis
  // —el que abre este mismo CTA— entregan constancia, y varios no entregan nada. Lo que
  // promete cada curso se decide con su `credentialType`, en la ficha.
  const meta = t("landing.launch.hero.meta", { returnObjects: true }) as string[];

  // El CTA principal lleva DIRECTO al curso destacado, no a un ancla. Antes apuntaba a
  // "#cursos": el botón prometía empezar el curso pero sólo movía la página, y en mobile
  // el botón real de inscripción quedaba abajo del fold — tres toques para algo ofrecido
  // en uno. Mismo criterio de "destacado" que FeaturedFreeCourse (isFeatured, si no el
  // primer gratis). Si todavía no cargó, cae al ancla para no dejar el CTA muerto.
  const { data: courses } = useAvailableCourses({ language: i18n.language as SupportedLanguages });
  const featured = courses?.find((c) => c.isFeatured) ?? courses?.find(isCourseFree);
  // El botón dice "Hacé el curso introductorio gratis" y abajo aclara "Empezás en 2 minutos":
  // tiene que INSCRIBIR, no llevar a leer un temario. Iba al detalle, que agregaba un paso
  // entre la promesa y el alta. Para ver el temario está el botón secundario de la tarjeta
  // del curso gratis, que es donde esa intención corresponde.
  const primaryHref = featured ? resolveDirectEnrollHref({ slug: featured.slug }) : "#cursos";

  return (
    <section className="relative overflow-hidden px-5 pb-28 pt-16 sm:px-8 md:flex md:min-h-screen md:items-center md:pb-40 md:pt-20">
      <HeroKineticField />
      <div className="relative z-10 mx-auto grid w-full max-w-[1280px] items-center gap-10 md:grid-cols-[1.25fr_0.85fr] md:gap-12">
        <div>
          <h1
            className="font-display text-[clamp(30px,4.9vw,54px)] font-extrabold leading-[1.08] tracking-tight text-landing-text"
            style={{ textWrap: "balance" }}
          >
            {t("landing.launch.hero.headlinePre")}
            {/* Resalte: barrido de marcador DETRÁS del texto + doble subrayado a mano.
                El círculo fino de antes se perdía sobre la crema; el marcador pesa. */}
            <span ref={circleRef} className="landing-draw relative inline-block">
              <span
                aria-hidden="true"
                className="absolute bottom-[8%] left-[-5px] right-[-5px] top-[14%] -rotate-[0.7deg] rounded-[4px]"
                style={{ background: "rgba(192,57,43,0.16)" }}
              />
              <span className="relative text-landing-red">
                {t("landing.launch.hero.headlineEm")}
              </span>
              <svg
                aria-hidden="true"
                viewBox="0 0 300 26"
                preserveAspectRatio="none"
                className="pointer-events-none absolute -bottom-[7px] left-0 h-[15px] w-full overflow-visible"
              >
                <path
                  pathLength="1"
                  d="M4,13 C 74,5 152,20 296,8"
                  className="fill-none stroke-landing-red"
                  strokeWidth="4.5"
                  strokeLinecap="round"
                />
                <path
                  pathLength="1"
                  d="M12,21 C 96,15 174,25 288,17"
                  className="fill-none stroke-landing-red opacity-45"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  style={{ animationDelay: "0.25s" }}
                />
              </svg>
            </span>
            {t("landing.launch.hero.headlinePost")}
          </h1>
          {/* El accionable aspiracional: era texto corrido de 17px y se perdía debajo del
              titular. Ahora es el sub-titular, con la promesa (desde cero / gratis) abajo. */}
          <p className="font-display mt-4 text-[clamp(20px,2.4vw,26px)] font-bold leading-snug text-landing-text">
            {t("landing.launch.hero.line")}
          </p>
          <p className="mt-1 text-[16px] text-landing-muted">{t("landing.launch.hero.lineSub")}</p>
          {/* UNA sola salida: el curso gratis. "Ver todos los cursos" salió del hero —
              el catálogo sigue en el CTA del header y en el footer, pero acá le
              competía a la única acción que importa. */}
          <div className="mt-6">
            <Link
              to={primaryHref}
              data-cuelume-press
              data-cuelume-release
              className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-landing-red px-7 py-4 text-[15.5px] font-bold text-white shadow-landing-red transition-all duration-150 hover:-translate-y-0.5 hover:bg-landing-red-hover hover:shadow-landing-red-hover sm:w-auto"
            >
              {t("landing.launch.hero.cta")} →
            </Link>
            {/* Microcopy de fricción cero, pegado al botón */}
            <p className="mt-2 text-[13px] text-landing-muted max-md:text-center">
              {t("landing.launch.hero.ctaNote")}
            </p>
          </div>
          {/* Metadata en small-caps con separador "·" (patrón 4.2 del manual). Antes
              eran 4 chips con check que quedaban 3+1 en desktop, y eran el quinto uso
              del mismo recurso en la página. */}
          {/* Adaptable: reflowea en varias líneas si hace falta, pero cada ítem viaja
              entero — sin `whitespace-nowrap` por ítem, "Credencial verificable" se
              partía al medio en mobile. Por eso también el ítem tiene que entrar en una
              línea de mobile: enumerar "constancia o certificado" acá desborda.
              El "·" viaja con el ítem que SIGUE, no con el anterior: así cuando la línea
              corta, el separador baja junto al ítem que separa en vez de quedar colgado
              al final del renglón de arriba. El espacio quebrable va afuera del span —
              dos spans nowrap adyacentes sin espacio entre medio no le dan al navegador
              dónde cortar, y la línea se desbordaba en vez de bajar. */}
          <p
            data-hero-floor
            className="landing-meta mt-7 max-w-[52ch] uppercase leading-relaxed tracking-[0.13em] text-landing-muted max-md:text-center"
          >
            {meta.map((item, i) => (
              <Fragment key={item}>
                {i > 0 ? " " : null}
                <span className="whitespace-nowrap">{i > 0 ? `· ${item}` : item}</span>
              </Fragment>
            ))}
          </p>
        </div>
        <HeroIllustration note={t("landing.launch.hero.illustrationNote")} />
      </div>
    </section>
  );
}
